import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import api from "../api/api";
import { analyzeML } from "../api/ml";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Legend,
  Tooltip,
  Filler
);

const PARAM_LABELS = {
  CO2: "CO₂",
  "PM2.5": "PM2.5",
  PM10: "PM10",
  PM1: "PM1",
  Temp: "Temperatura",
  Umid: "Umidade",
  Pressao: "Pressão",
  VOC: "VOC",
  NOx: "NOx",
  HCHO: "Formaldeído",
  CO: "CO",
  H2S: "H₂S",
};

const PROFILE_PARAMS = {
  HABITAT: ["CO2", "PM1", "PM2.5", "PM10", "VOC", "NOx", "Temp", "Umid", "Pressao"],
  MED: ["CO2", "PM1", "PM2.5", "PM10", "VOC", "HCHO", "Temp", "Umid", "Pressao"],
  PRO: ["PM1", "PM2.5", "PM10", "VOC", "NOx", "CO", "H2S", "Temp", "Umid"],
};

const FALLBACK_PARAMS = [
  { value: "CO2", label: "CO₂" },
  { value: "PM2.5", label: "PM2.5" },
  { value: "PM10", label: "PM10" },
  { value: "PM1", label: "PM1" },
  { value: "Temp", label: "Temperatura" },
  { value: "Umid", label: "Umidade" },
  { value: "Pressao", label: "Pressão" },
  { value: "VOC", label: "VOC" },
  { value: "NOx", label: "NOx" },
  { value: "HCHO", label: "Formaldeído" },
  { value: "CO", label: "CO" },
  { value: "H2S", label: "H₂S" },
];

const LS_KEYS = {
  filters: "iseq_ia_filters_v3",
  result: "iseq_ia_result_v3",
};

function saveJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function loadJSON(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function utcToLocalString(str) {
  if (!str || typeof str !== "string") return "-";
  const clean = str.replace("T", " ");
  const [date, time] = clean.split(" ");
  if (!date || !time) return "-";
  const [y, m, d] = date.split("-");
  const [hh = 0, mm = 0, ss = 0] = time.split(":");
  const dt = new Date(Date.UTC(+y, m - 1, +d, +hh, +mm, +ss));
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

function formatNumber(v, casas = 2) {
  if (v == null || !Number.isFinite(Number(v))) return "-";
  return Number(v).toFixed(casas).replace(".", ",");
}

function getPeriodError(periodo) {
  const ini = dayjs(periodo.inicio);
  const fim = dayjs(periodo.fim);
  if (!ini.isValid() || !fim.isValid()) return "Defina um período válido.";
  if (!fim.isAfter(ini)) return "A data final deve ser maior que a inicial.";
  if (fim.diff(ini, "day", true) > 7) return "O período máximo para análise é de 7 dias.";
  return "";
}

function buildEnvMap(rows = []) {
  const map = new Map();
  rows.forEach((row) => {
    const location = row?.location || row?.nomeExibicao || row?.mac;
    if (!location) return;

    const profile = row?.profile || "HABITAT";
    const caps = Array.isArray(row?.capabilities) && row.capabilities.length
      ? row.capabilities
      : PROFILE_PARAMS[profile] || PROFILE_PARAMS.HABITAT;

    if (!map.has(location)) {
      map.set(location, {
        location,
        macs: [],
        capabilities: new Set(),
      });
    }

    const slot = map.get(location);
    slot.macs.push({ mac: row.mac, capabilities: caps, profile });
    caps.forEach((c) => slot.capabilities.add(c));
  });
  return map;
}

function paramListFromEnvMap(envMap, selectedLocations = []) {
  const envs = selectedLocations.length
    ? selectedLocations.map((loc) => envMap.get(loc)).filter(Boolean)
    : Array.from(envMap.values());

  const set = new Set();
  envs.forEach((env) => env.capabilities.forEach((p) => set.add(p)));

  const ordered = FALLBACK_PARAMS.filter((p) => set.has(p.value));
  return ordered.length ? ordered : FALLBACK_PARAMS;
}

function chooseMacCandidates(envInfo, parametro) {
  if (!envInfo?.macs?.length) return [];
  const preferred = envInfo.macs
    .filter((m) => Array.isArray(m.capabilities) && m.capabilities.includes(parametro))
    .map((m) => m.mac);
  return preferred.length ? preferred : envInfo.macs.map((m) => m.mac);
}

function severityScore(item) {
  const resumo = item?.resumo || {};
  const status = resumo.status_operacional || "";
  const classif = resumo.classificacao_futura || "-";
  const classWeight =
    classif === "Alerta" ? 4 :
    classif === "Ruim" ? 3 :
    classif === "Atenção" ? 2 :
    classif === "Bom" ? 1 :
    classif === "Excelente" ? 0 : 1;
  const actionWeight =
    status === "Intervir agora" ? 3 :
    status === "Observar de perto" ? 1.5 : 0;
  const anomaly = Number(resumo.score_anomalia || 0);
  const dev = Math.abs(Number(resumo.desvio_percentual_baseline || 0)) / 100;
  return classWeight * 10 + actionWeight * 6 + anomaly * 8 + dev * 4;
}

function statusColor(status) {
  if (status === "Intervir agora") return "error";
  if (status === "Observar de perto") return "warning";
  return "success";
}

function confidenceWeight(label) {
  if (label === "alta") return 1;
  if (label === "média") return 0.65;
  return 0.35;
}

function confidenceLabel(avg) {
  if (avg >= 0.85) return "Alta";
  if (avg >= 0.55) return "Média";
  return "Baixa";
}

function KpiCard({ title, value, subtitle, tone = "default" }) {
  const border = tone === "danger" ? "error.main" : tone === "warning" ? "warning.main" : tone === "success" ? "success.main" : "divider";
  return (
    <Paper sx={{ p: 2.2, borderRadius: 3, border: 1, borderColor: border, minHeight: 122 }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{value}</Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Paper>
  );
}

function InsightRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, textAlign: "right" }}>{value}</Typography>
    </Stack>
  );
}

export default function IAMLPredicao() {
  const cachedFilters = loadJSON(LS_KEYS.filters, null);
  const cachedResult = loadJSON(LS_KEYS.result, null);

  const [tab, setTab] = useState(0);
  const [envMap, setEnvMap] = useState(new Map());
  const [ambientes, setAmbientes] = useState([]);
  const [ambientesSelecionados, setAmbientesSelecionados] = useState(cachedFilters?.ambientesSelecionados || []);
  const [parametros, setParametros] = useState(FALLBACK_PARAMS);
  const [parametro, setParametro] = useState(cachedFilters?.parametro || "CO2");
  const [periodo, setPeriodo] = useState({
    inicio: cachedFilters?.periodo?.inicio || dayjs().subtract(48, "hour").format("YYYY-MM-DDTHH:mm"),
    fim: cachedFilters?.periodo?.fim || dayjs().format("YYYY-MM-DDTHH:mm"),
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [data, setData] = useState(cachedResult?.data || {});
  const [selectedEnv, setSelectedEnv] = useState(cachedResult?.selectedEnv || "");

  useEffect(() => {
    async function loadCapabilities() {
      try {
        const [capsResp, eqResp] = await Promise.all([
          api.get("/equipment/capabilities"),
          api.get("/equipment"),
        ]);

        const capsRows = Array.isArray(capsResp?.data) ? capsResp.data : [];
        const eqRows = Array.isArray(eqResp?.data) ? eqResp.data : [];
        const rows = capsRows.length
          ? capsRows
          : eqRows.map((e) => ({
              mac: e.mac,
              location: e.location,
              profile: "HABITAT",
              capabilities: PROFILE_PARAMS.HABITAT,
            }));

        const map = buildEnvMap(rows);
        const envNames = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));
        setEnvMap(map);
        setAmbientes(envNames);

        const nextParams = paramListFromEnvMap(map, ambientesSelecionados);
        setParametros(nextParams);
        if (!nextParams.some((p) => p.value === parametro)) {
          setParametro(nextParams[0]?.value || "CO2");
        }
        if (!selectedEnv && envNames.length) setSelectedEnv(envNames[0]);
      } catch (err) {
        console.error("Erro ao carregar equipamentos da IA:", err);
        setAmbientes([]);
        setParametros(FALLBACK_PARAMS);
        setMsg("Não foi possível carregar os ambientes da análise.");
      }
    }
    loadCapabilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextParams = paramListFromEnvMap(envMap, ambientesSelecionados);
    setParametros(nextParams);
    if (!nextParams.some((p) => p.value === parametro)) {
      setParametro(nextParams[0]?.value || "");
    }
  }, [ambientesSelecionados, envMap, parametro]);

  useEffect(() => {
    saveJSON(LS_KEYS.filters, { ambientesSelecionados, parametro, periodo });
  }, [ambientesSelecionados, parametro, periodo]);

  const periodError = useMemo(() => getPeriodError(periodo), [periodo]);

  const ambientesPayload = useMemo(() => {
    const payload = {};
    ambientesSelecionados.forEach((loc) => {
      const envInfo = envMap.get(loc);
      payload[loc] = chooseMacCandidates(envInfo, parametro);
    });
    return payload;
  }, [ambientesSelecionados, envMap, parametro]);

  const envResults = useMemo(() => {
    return ambientesSelecionados
      .map((amb) => {
        const item = data?.[amb];
        if (!item || item.erro) return null;
        return { ambiente: amb, ...item };
      })
      .filter(Boolean);
  }, [data, ambientesSelecionados]);

  const ranking = useMemo(() => {
    return [...envResults]
      .map((item) => ({ ...item, criticidade: severityScore(item) }))
      .sort((a, b) => b.criticidade - a.criticidade);
  }, [envResults]);

  const overview = useMemo(() => {
    if (!ranking.length) return null;
    const principal = ranking[0];
    const emIntervencao = ranking.filter((r) => r?.resumo?.status_operacional === "Intervir agora").length;
    const emAtencao = ranking.filter((r) => r?.resumo?.status_operacional !== "Situação estável").length;
    const avgConfidence = ranking.reduce((acc, r) => acc + confidenceWeight(r?.resumo?.confianca), 0) / ranking.length;
    return {
      principal,
      emIntervencao,
      emAtencao,
      confiancaMedia: confidenceLabel(avgConfidence),
      confiancaMediaRaw: avgConfidence,
    };
  }, [ranking]);

  useEffect(() => {
    if (!selectedEnv && ranking.length) {
      setSelectedEnv(ranking[0].ambiente);
    } else if (selectedEnv && !ranking.some((r) => r.ambiente === selectedEnv) && ranking.length) {
      setSelectedEnv(ranking[0].ambiente);
    }
  }, [ranking, selectedEnv]);

  const selectedData = useMemo(
    () => ranking.find((r) => r.ambiente === selectedEnv) || ranking[0] || null,
    [ranking, selectedEnv]
  );

  const handleAnalyze = async () => {
    setMsg("");
    if (!ambientesSelecionados.length) {
      setMsg("Selecione ao menos um ambiente.");
      return;
    }
    if (!parametro) {
      setMsg("Selecione um parâmetro.");
      return;
    }
    if (periodError) {
      setMsg(periodError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ambientes: ambientesSelecionados,
        ambientes_macs: ambientesPayload,
        parametro,
        inicio: periodo.inicio,
        fim: periodo.fim,
        passo_minutos: 15,
        horizonte_horas: 6,
        treino_dias: 21,
      };

      const res = await analyzeML(payload);
      const result = res?.data || {};
      setData(result);

      const orderedEnvs = ambientesSelecionados
        .map((amb) => ({ ambiente: amb, ...(result?.[amb] || {}) }))
        .filter((item) => item?.resumo)
        .sort((a, b) => severityScore(b) - severityScore(a));
      const firstEnv = orderedEnvs[0]?.ambiente || ambientesSelecionados[0] || "";
      setSelectedEnv(firstEnv);
      saveJSON(LS_KEYS.result, { data: result, selectedEnv: firstEnv });

      if (!result || Object.keys(result).length === 0) {
        setMsg("A análise retornou sem dados.");
      }
    } catch (err) {
      console.error("Erro ao executar análise:", err);
      setMsg(
        err?.response?.data?.erro ||
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Não foi possível concluir a análise no momento."
      );
    } finally {
      setLoading(false);
    }
  };

  const chartData = useMemo(() => {
    if (!selectedData) return null;
    const historico = selectedData.historico || [];
    const baseline = selectedData.baseline || [];
    const faixa = selectedData.faixa_baseline || [];
    const previsao = selectedData.previsao || [];

    const histLabels = historico.map((p) => utcToLocalString(p.timestamp));
    const predLabels = previsao.map((p) => utcToLocalString(p.timestamp));
    const labels = [...histLabels, ...predLabels];

    const baselineMap = new Map(baseline.map((p) => [p.timestamp, p.valor]));
    const faixaMap = new Map(faixa.map((p) => [p.timestamp, p]));

    return {
      labels,
      datasets: [
        {
          label: "Faixa histórica (p25)",
          data: [...historico.map((p) => faixaMap.get(p.timestamp)?.p25 ?? null), ...new Array(previsao.length).fill(null)],
          borderColor: "rgba(0,0,0,0)",
          backgroundColor: "rgba(0,0,0,0)",
          pointRadius: 0,
          tension: 0.2,
          spanGaps: true,
        },
        {
          label: "Faixa histórica",
          data: [...historico.map((p) => faixaMap.get(p.timestamp)?.p75 ?? null), ...new Array(previsao.length).fill(null)],
          borderColor: "rgba(25,118,210,0)",
          backgroundColor: "rgba(25,118,210,0.10)",
          fill: "-1",
          pointRadius: 0,
          tension: 0.2,
          spanGaps: true,
        },
        {
          label: "Baseline",
          data: [...historico.map((p) => baselineMap.get(p.timestamp) ?? null), ...new Array(previsao.length).fill(null)],
          borderColor: "#43a047",
          backgroundColor: "#43a047",
          borderDash: [6, 6],
          pointRadius: 0,
          tension: 0.2,
          spanGaps: true,
        },
        {
          label: "Histórico",
          data: [...historico.map((p) => p.valor), ...new Array(previsao.length).fill(null)],
          borderColor: "#1976d2",
          backgroundColor: "#1976d2",
          pointRadius: 1.4,
          tension: 0.22,
          spanGaps: false,
        },
        {
          label: "Previsão",
          data: [...new Array(historico.length).fill(null), ...previsao.map((p) => p.valor)],
          borderColor: "#ef6c00",
          backgroundColor: "#ef6c00",
          borderDash: [8, 4],
          pointRadius: 1,
          tension: 0.22,
          spanGaps: true,
        },
      ],
    };
  }, [selectedData]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw == null ? "-" : formatNumber(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(255,255,255,0.08)" },
      },
    },
  }), []);

  const rankingBarData = useMemo(() => {
    if (!ranking.length) return null;
    return {
      labels: ranking.map((r) => r.ambiente),
      datasets: [
        {
          label: "Criticidade",
          data: ranking.map((r) => Number(r.criticidade.toFixed(2))),
          backgroundColor: ranking.map((r) =>
            r?.resumo?.status_operacional === "Intervir agora"
              ? "#d32f2f"
              : r?.resumo?.status_operacional === "Observar de perto"
              ? "#f9a825"
              : "#1976d2"
          ),
          borderRadius: 10,
          barThickness: 22,
        },
      ],
    };
  }, [ranking]);

  function exportarCSV() {
    if (!ranking.length) return;
    const rows = [];
    ranking.forEach((env) => {
      const resumo = env.resumo || {};
      (env.historico || []).forEach((p) => {
        rows.push({
          Ambiente: env.ambiente,
          Serie: "Historico",
          Horario: utcToLocalString(p.timestamp),
          Valor: p.valor,
          Parametro: parametro,
        });
      });
      (env.baseline || []).forEach((p) => {
        rows.push({
          Ambiente: env.ambiente,
          Serie: "Baseline",
          Horario: utcToLocalString(p.timestamp),
          Valor: p.valor,
          Parametro: parametro,
        });
      });
      (env.previsao || []).forEach((p) => {
        rows.push({
          Ambiente: env.ambiente,
          Serie: "Previsao",
          Horario: utcToLocalString(p.timestamp),
          Valor: p.valor,
          Parametro: parametro,
        });
      });
      rows.push({
        Ambiente: env.ambiente,
        Serie: "Resumo",
        Horario: "",
        Valor: resumo.valor_atual,
        Parametro: `${parametro} - atual`,
      });
    });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `analise_ia_${parametro}_${dayjs().format("YYYYMMDD_HHmm")}.csv`);
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: "auto", px: { xs: 1, md: 2 }, py: 2 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, mb: 2.5 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>Análise IA</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {PARAM_LABELS[parametro] || parametro}
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gap: 1.5, flex: 1, gridTemplateColumns: { xs: "1fr", md: "1.6fr 0.9fr 0.9fr 0.9fr 0.9fr" } }}>
            <FormControl fullWidth>
              <InputLabel>Ambientes</InputLabel>
              <Select
                multiple
                value={ambientesSelecionados}
                onChange={(e) => setAmbientesSelecionados(e.target.value)}
                renderValue={(selected) => selected.join(", ")}
                label="Ambientes"
              >
                {ambientes.map((a) => (
                  <MenuItem key={a} value={a}>
                    <Checkbox checked={ambientesSelecionados.indexOf(a) > -1} />
                    <ListItemText primary={a} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Parâmetro</InputLabel>
              <Select value={parametro || ""} onChange={(e) => setParametro(e.target.value)} label="Parâmetro">
                {parametros.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Início"
              type="datetime-local"
              value={periodo.inicio}
              onChange={(e) => setPeriodo((s) => ({ ...s, inicio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Fim"
              type="datetime-local"
              value={periodo.fim}
              onChange={(e) => setPeriodo((s) => ({ ...s, fim: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Stack direction="row" spacing={1} alignItems="stretch">
              <Button
                variant="contained"
                fullWidth
                onClick={handleAnalyze}
                disabled={loading || !ambientesSelecionados.length || !parametro || !!periodError}
                sx={{ borderRadius: 3, minHeight: 56 }}
              >
                {loading ? "Analisando..." : "Executar"}
              </Button>
              <Button variant="outlined" onClick={exportarCSV} disabled={!ranking.length || loading} sx={{ borderRadius: 3 }}>
                CSV
              </Button>
            </Stack>
          </Box>
        </Stack>

        {periodError ? (
          <Alert severity="warning" sx={{ mt: 2 }}>{periodError}</Alert>
        ) : null}
        {msg ? (
          <Alert severity="info" sx={{ mt: 2 }}>{msg}</Alert>
        ) : null}
      </Paper>

      {loading ? (
        <Paper sx={{ p: 6, borderRadius: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} color="text.secondary">Processando histórico, baseline e previsão.</Typography>
        </Paper>
      ) : null}


      {!loading && !ranking.length ? (
        <Paper sx={{ p: 5, borderRadius: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Pronto para analisar</Typography>
          <Typography color="text.secondary">Selecione os ambientes, o parâmetro e o período para gerar a leitura do comportamento do ambiente.</Typography>
        </Paper>
      ) : null}

      {!loading && ranking.length ? (
        <>
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, mb: 2.5 }}>
            <KpiCard title="Ambientes analisados" value={String(ranking.length)} subtitle={`${ambientesSelecionados.length} selecionados`} />
            <KpiCard
              title="Situação prioritária"
              value={overview?.principal?.ambiente || "-"}
              subtitle={overview?.principal?.resumo?.status_operacional || "-"}
              tone={overview?.principal?.resumo?.status_operacional === "Intervir agora" ? "danger" : overview?.principal?.resumo?.status_operacional === "Observar de perto" ? "warning" : "success"}
            />
            <KpiCard
              title="Ambientes em atenção"
              value={String(overview?.emAtencao || 0)}
              subtitle={`${overview?.emIntervencao || 0} com intervenção imediata`}
              tone={(overview?.emAtencao || 0) > 0 ? "warning" : "success"}
            />
            <KpiCard title="Confiança média" value={overview?.confiancaMedia || "-"} subtitle="Resultado agregado da análise" />
          </Box>

          <Paper sx={{ borderRadius: 4, overflow: "hidden" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1.5 }}>
              <Tab label="Painel" />
              <Tab label="Ambientes" />
              <Tab label="Ranking" />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 2fr) minmax(320px, 1fr)" } }}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedData?.ambiente || "-"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {PARAM_LABELS[parametro] || parametro}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={selectedData?.resumo?.classificacao_atual || "-"} size="small" />
                        <Chip label={selectedData?.resumo?.classificacao_futura || "-"} size="small" />
                        <Chip label={selectedData?.resumo?.tendencia_curta || "-"} size="small" />
                        <Chip label={selectedData?.resumo?.confianca || "-"} color={selectedData?.resumo?.confianca === "alta" ? "success" : selectedData?.resumo?.confianca === "média" ? "warning" : "default"} size="small" />
                        <Chip label={selectedData?.resumo?.status_operacional || "-"} color={statusColor(selectedData?.resumo?.status_operacional)} size="small" />
                      </Stack>
                    </Stack>

                    <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, mb: 2 }}>
                      <KpiCard title="Atual" value={formatNumber(selectedData?.resumo?.valor_atual)} />
                      <KpiCard title="Prev. 6h" value={formatNumber(selectedData?.resumo?.valor_previsto)} />
                      <KpiCard title="Baseline" value={formatNumber(selectedData?.resumo?.baseline_horario)} />
                      <KpiCard title="Desvio" value={`${formatNumber(selectedData?.resumo?.desvio_percentual_baseline)}%`} />
                      <KpiCard title="Cobertura" value={`${formatNumber(selectedData?.qualidade?.cobertura_periodo_pct, 1)}%`} />
                    </Box>

                    <Box sx={{ height: 360 }}>
                      {chartData ? <Line data={chartData} options={chartOptions} /> : null}
                    </Box>
                  </Paper>

                  <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Leitura operacional</Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>{selectedData?.explicacao || "-"}</Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Recomendação</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.7 }}>{selectedData?.recomendacao || "-"}</Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Qualidade da análise</Typography>
                      <Stack spacing={1.1}>
                        <InsightRow label="Modelo" value={selectedData?.qualidade?.tipo_modelo || "-"} />
                        <InsightRow label="Pontos observados" value={selectedData?.qualidade?.pontos_periodo ?? "-"} />
                        <InsightRow label="Pontos esperados" value={selectedData?.qualidade?.pontos_esperados ?? "-"} />
                        <InsightRow label="Amostras de treino" value={selectedData?.qualidade?.amostras_treino ?? "-"} />
                        <InsightRow label="Janela histórica" value={`${selectedData?.qualidade?.janela_treino_dias ?? "-"} dias`} />
                        <InsightRow label="Confiança" value={selectedData?.resumo?.confianca || "-"} />
                      </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Fatores associados</Typography>
                      {selectedData?.fatores?.length ? (
                        <Stack spacing={1}>
                          {selectedData.fatores.map((f) => (
                            <Paper key={`${f.sensor}-${f.correlacao}`} sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                <Typography sx={{ fontWeight: 700 }}>{PARAM_LABELS[f.sensor] || f.sensor}</Typography>
                                <Chip label={`${f.direcao} · ${f.forca}`} size="small" />
                              </Stack>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Correlação {formatNumber(f.correlacao, 3)} com o histórico recente.
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary">Sem associação forte identificada no período analisado.</Typography>
                      )}
                    </Paper>
                  </Stack>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Ambientes analisados</Typography>
                <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" } }}>
                  {ranking.map((item) => (
                    <Paper
                      key={item.ambiente}
                      variant="outlined"
                      onClick={() => {
                        setSelectedEnv(item.ambiente);
                        setTab(1);
                      }}
                      sx={{ p: 2, borderRadius: 3, cursor: "pointer", transition: "0.2s", '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography sx={{ fontWeight: 800 }}>{item.ambiente}</Typography>
                        <Chip label={item.resumo?.status_operacional || "-"} size="small" color={statusColor(item.resumo?.status_operacional)} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Atual {formatNumber(item.resumo?.valor_atual)} · Prev. {formatNumber(item.resumo?.valor_previsto)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.resumo?.classificacao_futura || "-"} · confiança {item.resumo?.confianca || "-"}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
                  <Box sx={{ width: { xs: "100%", lg: 260 }, flexShrink: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Ambientes</Typography>
                    <Stack spacing={1}>
                      {ranking.map((item) => (
                        <Paper
                          key={item.ambiente}
                          variant={selectedEnv === item.ambiente ? "elevation" : "outlined"}
                          onClick={() => setSelectedEnv(item.ambiente)}
                          sx={{ p: 1.5, borderRadius: 3, cursor: "pointer" }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>{item.ambiente}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.resumo?.status_operacional || "-"}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedData?.ambiente || "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">MAC: {selectedData?.mac || "-"}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip label={selectedData?.resumo?.classificacao_atual || "-"} size="small" />
                          <Chip label={selectedData?.resumo?.classificacao_futura || "-"} size="small" />
                          <Chip label={selectedData?.resumo?.status_operacional || "-"} color={statusColor(selectedData?.resumo?.status_operacional)} size="small" />
                        </Stack>
                      </Stack>

                      <Box sx={{ height: 380, mb: 2 }}>{chartData ? <Line data={chartData} options={chartOptions} /> : null}</Box>

                      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
                        <Paper sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}>
                          <Typography variant="subtitle2" color="text.secondary">Explicação</Typography>
                          <Typography sx={{ mt: 1, lineHeight: 1.7 }}>{selectedData?.explicacao || "-"}</Typography>
                        </Paper>
                        <Paper sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}>
                          <Typography variant="subtitle2" color="text.secondary">Recomendação</Typography>
                          <Typography sx={{ mt: 1, lineHeight: 1.7, fontWeight: 700 }}>{selectedData?.recomendacao || "-"}</Typography>
                        </Paper>
                        <Paper sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}>
                          <Typography variant="subtitle2" color="text.secondary">Aprendizado</Typography>
                          <Typography sx={{ mt: 1, lineHeight: 1.7 }}>{selectedData?.resumo?.aprendizado || "-"}</Typography>
                        </Paper>
                      </Box>
                    </Paper>
                  </Box>
                </Stack>
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1.3fr) minmax(0, 1fr)" } }}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Ranking de criticidade</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Ambiente</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Atual</TableCell>
                            <TableCell align="right">Prev.</TableCell>
                            <TableCell align="right">Anomalia</TableCell>
                            <TableCell align="right">Confiança</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ranking.map((item) => (
                            <TableRow key={item.ambiente} hover onClick={() => { setSelectedEnv(item.ambiente); setTab(1); }} sx={{ cursor: "pointer" }}>
                              <TableCell>{item.ambiente}</TableCell>
                              <TableCell>
                                <Chip label={item.resumo?.status_operacional || "-"} size="small" color={statusColor(item.resumo?.status_operacional)} />
                              </TableCell>
                              <TableCell align="right">{formatNumber(item.resumo?.valor_atual)}</TableCell>
                              <TableCell align="right">{formatNumber(item.resumo?.valor_previsto)}</TableCell>
                              <TableCell align="right">{formatNumber(item.resumo?.score_anomalia, 3)}</TableCell>
                              <TableCell align="right">{item.resumo?.confianca || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Comparativo</Typography>
                    <Box sx={{ height: 360 }}>{rankingBarData ? <Bar data={rankingBarData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { beginAtZero: true } } }} /> : null}</Box>
                  </Paper>
                </Box>
              </Box>
            )}
          </Paper>
        </>
      ) : null}
    </Box>
  );
}
